import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { TrendingUp, TrendingDown, Calendar, Clock } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function RespectGraph({ username, token, isDarkMode = false }) {
  const [historyData, setHistoryData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState('weekly') // 'weekly' or 'daily'

  useEffect(() => {
    fetchHistory()
  }, [username, viewMode])

  const fetchHistory = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(`${API}/respect/history/${username}?period=${viewMode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      
      // Format data for the chart
      const formattedData = response.data.map((item) => ({
        date: item.date,
        respect: item.respect_points,
        change: item.change,
      }))
      
      setHistoryData(formattedData)
    } catch (err) {
      console.error('Failed to fetch respect history:', err)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
        <CardContent className="py-8 text-center">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </CardContent>
      </Card>
    )
  }

  const hasPositiveChange = historyData.some(d => d.change > 0)
  const hasNegativeChange = historyData.some(d => d.change < 0)

  return (
    <Card data-testid="respect-graph" className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-100' : ''}`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            <TrendingUp className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            Respect Trend
          </CardTitle>
          <div className="flex gap-2">
            <Button
              data-testid="weekly-view-button"
              onClick={() => setViewMode('weekly')}
              variant={viewMode === 'weekly' ? 'default' : 'outline'}
              size="sm"
              className={viewMode === 'weekly' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Weekly
            </Button>
            <Button
              data-testid="daily-view-button"
              onClick={() => setViewMode('daily')}
              variant={viewMode === 'daily' ? 'default' : 'outline'}
              size="sm"
              className={viewMode === 'daily' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              <Clock className="w-4 h-4 mr-1" />
              Daily
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {historyData.length === 0 ? (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data available</p>
        ) : (
          <div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={historyData}>
                <defs>
                  <linearGradient id="colorRespect" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4b5563' : '#e5e7eb'} />
                <XAxis 
                  dataKey="date" 
                  stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                />
                <YAxis 
                  stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                  style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    border: isDarkMode ? '1px solid #4b5563' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: isDarkMode ? '#f3f4f6' : '#374151'
                  }}
                  labelStyle={{ fontWeight: 'bold', color: isDarkMode ? '#f3f4f6' : '#374151' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="respect" 
                  stroke="#6366f1" 
                  strokeWidth={3}
                  fill="url(#colorRespect)"
                  name="Respect Points"
                />
              </AreaChart>
            </ResponsiveContainer>
            
            {/* Legend */}
            <div className="mt-4 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{viewMode === 'weekly' ? 'Last 7 days' : 'Last 24 hours'}</span>
              </div>
              {hasPositiveChange && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Positive change</span>
                </div>
              )}
              {hasNegativeChange && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Negative change</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
